// src/hooks/useDeepLinks.js
import { useEffect } from 'react';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeApp } from '../config';
import logger from '../services/logger';

/**
 * Hook to handle deep links when app is opened from external links
 * @param {Function} onDeepLink - Callback function to handle the deep link
 */
export const useDeepLinks = (onDeepLink) => {
  useEffect(() => {
    if (!isNativeApp()) {
      return; // Only works in native app
    }

    let listener;

    const setupDeepLinks = async () => {
      try {
        // ✅ Dynamic import to avoid breaking web version
        const { App: CapacitorApp } = await import('@capacitor/app');

        // Listen for app URL open events
        listener = await CapacitorApp.addListener('appUrlOpen', (event) => {
          logger.info('App opened with URL', { url: event.url });
          
          // Parse the URL
          const url = new URL(event.url);
          const path = url.pathname;

          // Extract route information
          if (path.startsWith('/thread/')) {
            const threadId = path.split('/thread/')[1];
            onDeepLink({ type: 'thread', threadId });
          } else if (path.startsWith('/message/')) {
            const messageId = path.split('/message/')[1];
            onDeepLink({ type: 'message', messageId });
          } else {
            // Just open to dashboard
            onDeepLink({ type: 'home' });
          }
        });

        logger.debug('Deep link listener registered');
      } catch (error) {
        logger.error('Failed to setup deep links', error);
      }
    };

    setupDeepLinks();

    // Cleanup
    return () => {
      if (listener) {
        listener.remove();
      }
    };
  }, [onDeepLink]);
};

export default useDeepLinks;